import React, { useState, useEffect } from 'react';
import { openPrintWindow } from '../../utils/printDocument';
// Correction des erreurs calculateAbsenceDays -> calculateAbsenceDuration
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Badge,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventBusy as EventBusyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  CalendarMonth as CalendarTodayIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  orderBy,
  Timestamp,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';

const AbsenceDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [establishmentInfo, setEstablishmentInfo] = useState(null);

  const fetchEstablishmentInfo = async () => {
    if (userData?.establishmentId) {
      try {
        const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
        if (establishmentDoc.exists()) {
          const data = establishmentDoc.data();
          setEstablishmentInfo(data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des informations de l\'établissement:', error);
      }
    }
  };

  const [absences, setAbsences] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    period: 'current_month',
    search: ''
  });
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [validationComment, setValidationComment] = useState('');
  const [validationAction, setValidationAction] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    dateDebut: '',
    dateFin: '',
    type: '',
    motif: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    totalDays: 0,
    currentMonth: 0
  });

  // Charger les données
  useEffect(() => {
    if (currentUser) {
      fetchData();
      fetchEstablishmentInfo();
    }
  }, [currentUser, filters.status, filters.type, filters.period]);

  const [allAbsences, setAllAbsences] = useState([]);

  // Effet séparé pour la recherche côté client
  useEffect(() => {
    if (allAbsences.length > 0) {
      applyClientSideFilters();
    }
  }, [filters.search, allAbsences]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAbsences(),
        fetchPersonnel()
      ]);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des données',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAbsences = async () => {
    try {
      // Récupérer toutes les absences sans filtre de période
      // Le filtre de période et le tri seront appliqués côté client
      let q = query(
        collection(db, 'absences'),
        where('chefServiceId', '==', currentUser.uid)
      );

      const querySnapshot = await getDocs(q);
      let absencesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Trier les absences par date de création (plus récentes en premier)
      absencesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB - dateA; // Ordre décroissant (plus récentes en premier)
      });

      // Optimisation : Récupérer tous les personnels en une seule requête
      const uniquePersonnelIds = [...new Set(absencesData.map(a => a.personnelId).filter(Boolean))];
      const personnelCache = new Map();
      
      if (uniquePersonnelIds.length > 0) {
        try {
          // Récupérer tous les personnels du chef de service en une seule requête
          const personnelQuery = query(
            collection(db, 'users'),
            where('chefServiceId', '==', currentUser.uid)
          );
          const personnelSnapshot = await getDocs(personnelQuery);
          
          personnelSnapshot.docs.forEach(doc => {
            const data = doc.data();
            personnelCache.set(doc.id, {
              firstName: data.firstName,
              lastName: data.lastName,
              service: data.service
            });
          });
        } catch (error) {
          console.error('Erreur lors de la récupération du personnel:', error);
        }
      }

      // Enrichir les données avec les informations du personnel depuis le cache
      absencesData.forEach(absence => {
        if (absence.personnelId && personnelCache.has(absence.personnelId)) {
          const personnelData = personnelCache.get(absence.personnelId);
          absence.nomPersonnel = personnelData.firstName;
          absence.prenomPersonnel = personnelData.lastName;
          absence.service = personnelData.service;
        }
      });

      // Stocker toutes les absences pour le filtrage côté client
      setAllAbsences(absencesData);
      
      // Appliquer les filtres
      applyClientSideFilters(absencesData);
    } catch (error) {
      console.error('Erreur lors de la récupération des absences:', error);
    }
  };

  const applyClientSideFilters = (data = allAbsences) => {
    let filteredData = [...data];

    // Filtrer par période
    if (filters.period === 'current_month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      filteredData = filteredData.filter(absence => {
        const absenceDate = absence.dateDebut?.toDate ? absence.dateDebut.toDate() : new Date(absence.dateDebut);
        return absenceDate >= startOfMonth && absenceDate <= endOfMonth;
      });
    } else if (filters.period === 'current_year') {
      const currentYear = new Date().getFullYear();
      
      filteredData = filteredData.filter(absence => {
        const absenceDate = absence.dateDebut?.toDate ? absence.dateDebut.toDate() : new Date(absence.dateDebut);
        return absenceDate.getFullYear() === currentYear;
      });
    }
    // Si filters.period === 'all', on ne filtre pas par période

    // Filtrer par statut
    if (filters.status !== 'all') {
      filteredData = filteredData.filter(absence => absence.statut === filters.status);
    }

    // Filtrer par type
    if (filters.type !== 'all') {
      filteredData = filteredData.filter(absence => absence.type === filters.type);
    }

    // Filtrer par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filteredData = filteredData.filter(absence => 
        absence.nomPersonnel?.toLowerCase().includes(searchLower) ||
        absence.prenomPersonnel?.toLowerCase().includes(searchLower) ||
        absence.motif?.toLowerCase().includes(searchLower)
      );
    }

    setAbsences(filteredData);
    calculateStats(filteredData);
  };

  const fetchPersonnel = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('chefServiceId', '==', currentUser.uid),
        where('role', '==', 'personnel')
      );

      const querySnapshot = await getDocs(q);
      const personnelData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setPersonnel(personnelData);
    } catch (error) {
      console.error('Erreur lors de la récupération du personnel:', error);
    }
  };

  const calculateStats = (absencesData = absences) => {
    const pending = absencesData.filter(a => a.statut === 'en attente').length;
    const approved = absencesData.filter(a => a.statut === 'approuvée').length;
    const rejected = absencesData.filter(a => a.statut === 'refusée').length;
    
    const totalDays = absencesData.reduce((total, absence) => {
      try {
        if (!absence.dateDebut || !absence.dateFin) return total;
        
        const start = absence.dateDebut?.toDate ? absence.dateDebut.toDate() : new Date(absence.dateDebut);
        const end = absence.dateFin?.toDate ? absence.dateFin.toDate() : new Date(absence.dateFin);
        
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return total;
        
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return total + diffDays;
      } catch (error) {
        console.error('Erreur calcul durée:', error, absence);
        return total;
      }
    }, 0);

    // Absences du mois en cours
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentMonthAbsences = absencesData.filter(absence => {
      try {
        if (!absence.dateDebut) return false;
        const absenceDate = absence.dateDebut?.toDate ? absence.dateDebut.toDate() : new Date(absence.dateDebut);
        if (isNaN(absenceDate.getTime())) return false;
        return absenceDate.getMonth() === currentMonth && absenceDate.getFullYear() === currentYear;
      } catch (error) {
        console.error('Erreur filtre mois:', error, absence);
        return false;
      }
    }).length;

    setStats({
      pending,
      approved,
      rejected,
      totalDays,
      currentMonth: currentMonthAbsences
    });
  };

  const handleValidateAbsence = (absence, action) => {
    setSelectedAbsence(absence);
    setValidationAction(action);
    setOpenValidationDialog(true);
  };

  const handleEditAbsence = (absence) => {
    setSelectedAbsence(absence);
    setEditFormData({
      dateDebut: absence.dateDebut || '',
      dateFin: absence.dateFin || '',
      type: absence.type || '',
      motif: absence.motif || ''
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedAbsence(null);
    setEditFormData({
      dateDebut: '',
      dateFin: '',
      type: '',
      motif: ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const absenceRef = doc(db, 'absences', selectedAbsence.id);
      await updateDoc(absenceRef, {
        dateDebut: editFormData.dateDebut,
        dateFin: editFormData.dateFin,
        type: editFormData.type,
        motif: editFormData.motif,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });

      setSnackbar({
        open: true,
        message: 'Absence modifiée avec succès',
        severity: 'success'
      });

      handleCloseEditDialog();
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la modification',
        severity: 'error'
      });
    }
  };

  const handlePrintAbsence = async (absence) => {
    // Récupérer les informations du personnel
    let personnelInfo = null;
    try {
      const personnelDoc = await getDoc(doc(db, 'users', absence.personnelId));
      if (personnelDoc.exists()) {
        personnelInfo = personnelDoc.data();
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des informations du personnel:', error);
    }

    // Déterminer le titre selon le type
    const isSignalement = absence.nature === 'signalement';
    const avecJustificatif = absence.avecJustificatif || false;
    const titre = isSignalement 
      ? (avecJustificatif ? 'SIGNALEMENT D\'ARRÊT AVEC JUSTIFICATIF' : 'SIGNALEMENT D\'ABSENCE')
      : 'DEMANDE D\'ABSENCE';

    const extraCss = `
            .print-page {
              font-size: 12px;
              line-height: 1.4;
              color: #333;
            }
            .header { 
              text-align: center; 
              margin-bottom: 15px; 
              padding: 8px;
              border-bottom: 2px solid #333;
            }
            .school-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 3px;
            }
            .school-info {
              font-size: 11px;
              margin-bottom: 2px;
            }
            .section { 
              margin-bottom: 12px; 
              padding: 8px;
              border: 1px solid #ddd;
              border-radius: 3px;
            }
            .section h2 {
              font-size: 14px;
              margin: 0 0 8px 0;
              color: #2c3e50;
            }
            .section h3 {
              font-size: 13px;
              margin: 0 0 6px 0;
              color: #34495e;
            }
            .info-line { 
              margin: 6px 0;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 1px dotted #ccc;
              padding-bottom: 3px;
              min-height: 18px;
            }
            .label {
              font-weight: bold;
              width: 35%;
              font-size: 11px;
            }
            .value {
              width: 65%;
              font-size: 11px;
              text-align: right;
            }
            .status {
              padding: 3px 8px;
              border-radius: 12px;
              font-weight: bold;
              text-align: center;
              display: inline-block;
              font-size: 10px;
            }
            .status.en-attente {
              background-color: #fff3cd;
              color: #856404;
              border: 1px solid #ffeaa7;
            }
            .status.approuvee {
              background-color: #d4edda;
              color: #155724;
              border: 1px solid #c3e6cb;
            }
            .status.refusee {
              background-color: #f8d7da;
              color: #721c24;
              border: 1px solid #f5c6cb;
            }
            .signatures {
              margin-top: 20px;
              display: flex;
              justify-content: space-between;
              page-break-inside: avoid;
            }
            .signature-box {
              width: 45%;
              text-align: center;
            }
            .signature-line {
              margin-top: 50px;
              border-top: 1px solid #000;
              padding-top: 3px;
              font-weight: bold;
              font-size: 10px;
            }
            .date-line {
              margin-top: 10px;
              font-size: 9px;
            }
            .motif-section {
              background-color: #f8f9fa;
              padding: 8px;
              border-left: 3px solid #007bff;
              margin: 8px 0;
            }
            .motif-section h3 {
              margin-bottom: 4px;
            }
            .motif-section p {
              margin: 0;
              font-size: 11px;
              line-height: 1.3;
            }
            /* Responsive adjustments */
            @media print {
              .section {
                break-inside: avoid;
              }
              .signatures {
                break-inside: avoid;
              }
            }
    `;

    const bodyHtml = `
          <div class="print-page">
          <div class="header">
            <div class="school-name">${establishmentInfo?.name || 'Établissement scolaire'}</div>
            ${establishmentInfo?.academie ? `<div class="school-info">Académie: ${establishmentInfo.academie}</div>` : ''}
            ${establishmentInfo?.anneeScolaire ? `<div class="school-info">Année scolaire: ${establishmentInfo.anneeScolaire}</div>` : ''}
            <h1>${titre}</h1>
          </div>
          
          <div class="section">
            <h2>Informations du Personnel</h2>
            <div class="info-line">
              <span class="label">Nom complet:</span>
              <span class="value">${personnelInfo?.firstName || absence.nomPersonnel || 'Personnel'} ${personnelInfo?.lastName || absence.prenomPersonnel || ''}</span>
            </div>
            <div class="info-line">
              <span class="label">Service:</span>
              <span class="value">${personnelInfo?.service || absence.service || 'Non spécifié'}</span>
            </div>
          </div>

          <div class="section">
            <h2>Détails de l'Absence</h2>
            <div class="info-line">
              <span class="label">Type d'absence:</span>
              <span class="value">${absence.type}</span>
            </div>
            <div class="info-line">
              <span class="label">Date de début:</span>
              <span class="value">${formatDate(absence.dateDebut)}</span>
            </div>
            <div class="info-line">
              <span class="label">Date de fin:</span>
              <span class="value">${formatDate(absence.dateFin)}</span>
            </div>
            ${absence.heureDebut && absence.heureFin ? `
            <div class="info-line">
              <span class="label">Horaires:</span>
              <span class="value">${absence.heureDebut} - ${absence.heureFin}</span>
            </div>
            ` : ''}
            <div class="info-line">
              <span class="label">Durée:</span>
              <span class="value">${calculateAbsenceDuration(absence)}</span>
            </div>
            <div class="info-line">
              <span class="label">Statut:</span>
              <span class="value"><span class="status ${absence.statut.replace(' ', '-')}">${absence.statut.toUpperCase()}</span></span>
            </div>
            <div class="info-line">
              <span class="label">Date de demande:</span>
              <span class="value">${formatDate(absence.createdAt)}</span>
            </div>
            ${avecJustificatif ? `
            <div class="info-line">
              <span class="label">Justificatif:</span>
              <span class="value">✓ Avec justificatif médical</span>
            </div>
            ` : ''}
          </div>

          ${absence.motif ? `
          <div class="motif-section">
            <h3>Motif de l'absence</h3>
            <p>${absence.motif}</p>
          </div>
          ` : ''}

          ${isSignalement && avecJustificatif ? `
          <div class="motif-section" style="background-color: #fff3cd; border-left: 3px solid #ffc107;">
            <h3 style="color: #856404;">⚠️ Justificatif Médical Requis</h3>
            <p style="color: #856404;">Cette absence est justifiée par un document médical officiel (arrêt maladie, certificat médical, etc.). Le justificatif doit être fourni dans les meilleurs délais.</p>
          </div>
          ` : ''}

          ${!isSignalement ? `
          <div class="signatures">
            <div class="signature-box">
              <div>Date et signature de l'agent</div>
              <div class="signature-line">${personnelInfo?.firstName || absence.nomPersonnel || 'Personnel'} ${personnelInfo?.lastName || absence.prenomPersonnel || ''}</div>
              <div class="date-line">Date: _______________</div>
            </div>
            <div class="signature-box">
              <div>Date et signature du chef de service</div>
              <div class="signature-line">${userData?.firstName || ''} ${userData?.lastName || ''}</div>
              <div class="date-line">Date: _______________</div>
            </div>
          </div>
          ` : ''}
          </div>`;

    openPrintWindow(
      `Fiche d'Absence - ${personnelInfo?.firstName || absence.nomPersonnel || 'Personnel'} ${personnelInfo?.lastName || absence.prenomPersonnel || ''}`,
      bodyHtml,
      extraCss,
      'height=900,width=1000',
      { singlePage: false, pageMargin: '10mm 8mm' }
    );
  };

  const submitValidation = async () => {
    if (!selectedAbsence) return;

    try {
      const newStatus = validationAction === 'approve' ? 'approuvée' : 'refusée';
      
      await updateDoc(doc(db, 'absences', selectedAbsence.id), {
        statut: newStatus,
        validatedBy: currentUser.uid,
        validatedAt: serverTimestamp(),
        validationComment: validationComment || null,
        updatedAt: serverTimestamp()
      });

      setSnackbar({
        open: true,
        message: `Absence ${newStatus} avec succès`,
        severity: 'success'
      });

      setOpenValidationDialog(false);
      setValidationComment('');
      await fetchData(); // Recharger les données
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la validation',
        severity: 'error'
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('fr-FR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en attente': return 'warning';
      case 'approuvée': return 'success';
      case 'refusée': return 'error';
      case 'signalée': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'en attente': return <PendingIcon />;
      case 'approuvée': return <CheckCircleIcon />;
      case 'refusée': return <CancelIcon />;
      case 'signalée': return <WarningIcon />;
      default: return <EventBusyIcon />;
    }
  };

  const calculateAbsenceDuration = (absence) => {
    // Utiliser les nouveaux champs si disponibles
    if (absence.touteLaJournee !== undefined) {
      if (absence.touteLaJournee) {
        return 'Toute la journée';
      } else {
        // Afficher les heures si disponibles, sinon le nombre d'heures
        if (absence.heureDebut && absence.heureFin) {
          return `${absence.heureDebut} - ${absence.heureFin} (${absence.nombreHeures || 'Non spécifié'} h)`;
        } else {
          return `${absence.nombreHeures || 'Non spécifié'} heure(s)`;
        }
      }
    }
    
    // Fallback pour les anciennes absences : calculer en jours
    const start = absence.dateDebut?.toDate() || new Date(absence.dateDebut);
    const end = absence.dateFin?.toDate() || new Date(absence.dateFin);
    const diffTime = Math.abs(end - start);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return `${days} jour(s)`;
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chef-service/dashboard')}
        >
          Retour au tableau de bord
        </Button>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventBusyIcon color="primary" />
          Gestion des Absences
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CalendarTodayIcon />}
          onClick={() => navigate('/chef-service/absences/calendar')}
        >
          Vue Calendrier
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4">{stats.pending}</Typography>
                  <Typography variant="body2">En attente</Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4">{stats.approved}</Typography>
                  <Typography variant="body2">Approuvées</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4">{stats.rejected}</Typography>
                  <Typography variant="body2">Refusées</Typography>
                </Box>
                <CancelIcon sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4">{stats.totalDays}</Typography>
                  <Typography variant="body2">Jours total</Typography>
                </Box>
                <CalendarTodayIcon sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon />
          Filtres
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Rechercher..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="en attente">En attente</MenuItem>
                <MenuItem value="approuvée">Approuvées</MenuItem>
                <MenuItem value="refusée">Refusées</MenuItem>
                <MenuItem value="signalée">Signalées</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="congé">Congé</MenuItem>
                <MenuItem value="maladie">Maladie</MenuItem>
                <MenuItem value="formation">Formation</MenuItem>
                <MenuItem value="autre">Autre</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Période</InputLabel>
              <Select
                value={filters.period}
                onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value }))}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="current_month">Mois actuel</MenuItem>
                <MenuItem value="current_year">Année actuelle</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Onglets */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={stats.pending} color="warning">
                Demandes en attente
              </Badge>
            } 
          />
          <Tab label="Toutes les absences" />
          <Tab label="Statistiques" />
        </Tabs>

        {/* Onglet Demandes en attente */}
        <TabPanel value={selectedTab} index={0}>
          {absences.filter(a => a.statut === 'en attente').length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune demande en attente
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ p: 2 }}>
              {absences.filter(a => a.statut === 'en attente').map((absence) => (
                <Grid item xs={12} md={6} key={absence.id}>
                  <Card sx={{ border: '1px solid', borderColor: 'warning.main' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6">
                            {absence.nomPersonnel} {absence.prenomPersonnel}
                          </Typography>
                          <Chip 
                            label={absence.type} 
                            size="small" 
                            color="primary" 
                            sx={{ mt: 1 }} 
                          />
                        </Box>
                        <Chip 
                          icon={getStatusIcon(absence.statut)}
                          label={absence.statut}
                          color={getStatusColor(absence.statut)}
                          size="small"
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Du {formatDate(absence.dateDebut)} au {formatDate(absence.dateFin)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Durée:</strong> {calculateAbsenceDuration(absence)}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Motif:</strong> {absence.motif}
                      </Typography>
                      {absence.nature && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Nature:</strong> {absence.nature}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleValidateAbsence(absence, 'reject')}
                      >
                        Refuser
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleValidateAbsence(absence, 'approve')}
                      >
                        Approuver
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Onglet Toutes les absences */}
        <TabPanel value={selectedTab} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Personnel</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Période</TableCell>
                  <TableCell>Durée</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {absences.map((absence) => (
                  <TableRow key={absence.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {absence.nomPersonnel} {absence.prenomPersonnel}
                        </Typography>
                        {absence.motif && (
                          <Typography variant="caption" color="text.secondary">
                            {absence.motif.substring(0, 50)}...
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={absence.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(absence.dateDebut)} - {formatDate(absence.dateFin)}
                      </Typography>
                    </TableCell>
                    <TableCell>{calculateAbsenceDuration(absence)}</TableCell>
                    <TableCell>
                      <Chip 
                        icon={getStatusIcon(absence.statut)}
                        label={absence.statut}
                        color={getStatusColor(absence.statut)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {absence.statut === 'en attente' && (
                          <>
                            <Tooltip title="Approuver">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleValidateAbsence(absence, 'approve')}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Refuser">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleValidateAbsence(absence, 'reject')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {absence.nature === 'signalement' && (
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleEditAbsence(absence)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Imprimer la fiche">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePrintAbsence(absence)}
                          >
                            <PrintIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {absences.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        Aucune absence trouvée
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Onglet Statistiques */}
        <TabPanel value={selectedTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon />
                  Résumé mensuel
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1">
                    Absences ce mois: <strong>{stats.currentMonth}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Total jours d'absence: <strong>{stats.totalDays}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Personnel actif: <strong>{personnel.length}</strong>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon />
                  Répartition par type
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {['congé', 'maladie', 'formation', 'autre'].map(type => {
                    const count = absences.filter(a => a.type === type).length;
                    return (
                      <Typography key={type} variant="body1">
                        {type.charAt(0).toUpperCase() + type.slice(1)}: <strong>{count}</strong>
                      </Typography>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Dialog de validation */}
      <Dialog open={openValidationDialog} onClose={() => setOpenValidationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {validationAction === 'approve' ? 'Approuver l\'absence' : 'Refuser l\'absence'}
        </DialogTitle>
        <DialogContent>
          {selectedAbsence && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Personnel:</strong> {selectedAbsence.nomPersonnel} {selectedAbsence.prenomPersonnel}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Période:</strong> {formatDate(selectedAbsence.dateDebut)} - {formatDate(selectedAbsence.dateFin)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {selectedAbsence.type}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Motif:</strong> {selectedAbsence.motif}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Commentaire (optionnel)"
            value={validationComment}
            onChange={(e) => setValidationComment(e.target.value)}
            placeholder="Ajoutez un commentaire sur votre décision..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenValidationDialog(false)}>
            Annuler
          </Button>
          <Button
            onClick={submitValidation}
            variant="contained"
            color={validationAction === 'approve' ? 'success' : 'error'}
          >
            {validationAction === 'approve' ? 'Approuver' : 'Refuser'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog d'édition */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Modifier l'absence signalée
        </DialogTitle>
        <DialogContent>
          {selectedAbsence && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Personnel:</strong> {selectedAbsence.nomPersonnel} {selectedAbsence.prenomPersonnel}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date de début"
                    type="date"
                    value={editFormData.dateDebut}
                    onChange={(e) => setEditFormData({ ...editFormData, dateDebut: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date de fin"
                    type="date"
                    value={editFormData.dateFin}
                    onChange={(e) => setEditFormData({ ...editFormData, dateFin: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Type d'absence</InputLabel>
                    <Select
                      value={editFormData.type}
                      onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
                      label="Type d'absence"
                    >
                      <MenuItem value="Maladie">Maladie</MenuItem>
                      <MenuItem value="Congé">Congé</MenuItem>
                      <MenuItem value="Absence injustifiée">Absence injustifiée</MenuItem>
                      <MenuItem value="Formation">Formation</MenuItem>
                      <MenuItem value="Autre">Autre</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Motif"
                    value={editFormData.motif}
                    onChange={(e) => setEditFormData({ ...editFormData, motif: e.target.value })}
                    placeholder="Décrivez le motif de l'absence..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            color="primary"
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AbsenceDashboard;
